import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PieController,
  DoughnutController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
// 1. Import the datalabels plugin
import DatalabelsPlugin from "chartjs-plugin-datalabels";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  Users,
  FileText,
  Filter,
  ArchiveIcon,
} from "lucide-react";
import { useAnalytics } from "../../customHooks/Analytics/useAnalytics";

// 2. Register the datalabels plugin
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PieController,
  DoughnutController,
  Title,
  Tooltip,
  Legend,
  DatalabelsPlugin // <-- REGISTERED PLUGIN
);
const ISSUE_LEVELS = ["ISSUE", "GRIEVANCE"];
export default function Analytics() {
  const [activeTab, setActiveTab] = useState("REPORTED");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [issueLevel, setIssueLevel] = useState("ALL");

  const { analytics, loading } = useAnalytics(activeTab, {
    created_from: createdFrom,
    created_to: createdTo,
    issue_level: issueLevel === "ALL" ? "" : issueLevel, // Pass empty string if ALL is selected
  });

  const statusChartRef = useRef(null);
  const levelChartRef = useRef(null);
  const categoryChartRef = useRef(null);
  const resolutionChartRef = useRef(null); // <-- Added ref for completeness

  const statusChartInstance = useRef(null);
  const levelChartInstance = useRef(null);
  const categoryChartInstance = useRef(null);
  const resolutionChartInstance = useRef(null);

  const createTimeoutRef = useRef(null);

  const key = analytics?.key || activeTab;
  const data = analytics?.data ?? {
    total_issues: 0,
    escalated_issues: 0,
    resolved_issues: 0,
    processing_issues: 0,
    closed_issues: 0,
    open_issues: 0,
    sla_breach_percent: 0,
    avg_resolution_time_hours: 0,
    IssuelevelPercentage: {},
    IssueCategoryPercentage: {},
    IssueStatusPercentage: {},
    ResolutionCounts: {},
    LatestActionInsights: [],
  };

  const categoryEntries = useMemo(() => {
    const entries = Object.entries(data.IssueCategoryPercentage || {});
    return entries.filter(([, val]) => Number(val) > 0);
  }, [data]);

  const hasCategoryData = categoryEntries.length > 0;

  const resolutionEntries = useMemo(() => {
    const entries = Object.entries(data.ResolutionCounts || {});
    return entries.filter(([, val]) => Number(val) > 0);
  }, [data]);

  const hasResolutionData = resolutionEntries.length > 0;

  const stats = useMemo(
    () =>
      [
        { title: "Total Issues", value: data.total_issues, icon: FileText },
        { title: "Open Issues", value: data.open_issues, icon: AlertCircle },
        { title: "Processing", value: data.processing_issues, icon: Clock },
        { title: "Resolved", value: data.closed_issues, icon: CheckCircle },
        { title: "Closed", value: data.resolved_issues, icon: ArchiveIcon },


        { title: "Escalated", value: data.escalated_issues, icon: TrendingUp },
       {
     title: "Avg Resolution (hrs)",
     value: Number(data.avg_resolution_time_hours || 0).toFixed(2),
     icon: Users,
    },
        activeTab === "COUNTERPARTY"
          ? {
              title: "SLA Breach (%)",
              value: Number(data.sla_breach_percent || 0).toFixed(2) + "%",
              icon: AlertCircle,
            }
          : null,
      ].filter(Boolean), // remove null items
    [data, activeTab]
  );

  useEffect(() => {
    const destroyAll = () => {
      if (statusChartInstance.current) {
        try {
          statusChartInstance.current.destroy();
        } catch {}
        statusChartInstance.current = null;
      }
      if (levelChartInstance.current) {
        try {
          levelChartInstance.current.destroy();
        } catch {}
        levelChartInstance.current = null;
      }
      if (categoryChartInstance.current) {
        try {
          categoryChartInstance.current.destroy();
        } catch {}
        categoryChartInstance.current = null;
      }
      if (resolutionChartInstance.current) {
        try {
          resolutionChartInstance.current.destroy();
        } catch (e) {
          console.error("Error destroying resolution chart:", e);
        }
        resolutionChartInstance.current = null;
      }
    };

    if (createTimeoutRef.current) {
      clearTimeout(createTimeoutRef.current);
      createTimeoutRef.current = null;
    }

    if (!analytics || !analytics.data) {
      destroyAll();
      return;
    }

    destroyAll();

    createTimeoutRef.current = setTimeout(() => {
      try {
        if (statusChartRef.current) {
          const ctx = statusChartRef.current.getContext("2d");
          statusChartInstance.current = new ChartJS(ctx, {
            type: "pie",
            data: {
              labels: Object.keys(data.IssueStatusPercentage || {}),
              datasets: [
                {
                  data: Object.values(data.IssueStatusPercentage || {}),
                  backgroundColor: ["#F59E0B", "#3B82F6", "#10B981", "#6B7280"],
                  borderColor: "#fff",
                  borderWidth: 1,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  callbacks: {
                    label: (ctx) =>
                      `${ctx.label}: ${Number(ctx.parsed).toFixed(2)}%`,
                  },
                },
                // 3. Add datalabels config for Issue Status (Pie Chart)
                datalabels: {
                  color: "#fff",
                  font: {
                    weight: "bold",
                    size: 10,
                  },
                  formatter: (value) => {
                    // Only show non-zero percentage
                    return value > 0.01 ? `${value.toFixed(1)}%` : "";
                  },
                  display: true,
                },
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to create status chart:", e);
      }

      try {
        if (levelChartRef.current) {
          const ctx = levelChartRef.current.getContext("2d");
          levelChartInstance.current = new ChartJS(ctx, {
            type: "pie",
            data: {
              labels: Object.keys(data.IssuelevelPercentage || {}),
              datasets: [
                {
                  data: Object.values(data.IssuelevelPercentage || {}),
                  backgroundColor: [
                    "#3B82F6",
                    "#10B981",
                    "#F59E0B",
                    "#EF4444",
                    "#8B5CF6",
                    "#EC4899",
                  ],
                  borderColor: "#fff",
                  borderWidth: 1,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  callbacks: {
                    label: (ctx) =>
                      `${ctx.label}: ${Number(ctx.parsed).toFixed(2)}%`,
                  },
                },
                // 3. Add datalabels config for Issue Level (Pie Chart)
                datalabels: {
                  color: "#fff",
                  font: {
                    weight: "bold",
                    size: 10,
                  },
                  formatter: (value) => {
                    // Only show non-zero percentage
                    return value > 0.01 ? `${value.toFixed(1)}%` : "";
                  },
                  display: true,
                },
              },
            },
          });
        }
      } catch (e) {}

      try {
        if (hasResolutionData && resolutionChartRef.current) {
          const ctx = resolutionChartRef.current.getContext("2d");
          resolutionChartInstance.current = new ChartJS(ctx, {
            type: "doughnut",
            data: {
              labels: resolutionEntries.map(([k]) => k),
              datasets: [
                {
                  label: "Count",
                  data: resolutionEntries.map(([_, v]) => v),
                  backgroundColor: [
                    "#10B981",
                    "#F59E0B",
                    "#EF4444",
                    "#6B7280",
                    "#3B82F6",
                  ],
                  borderColor: "#fff",
                  borderWidth: 1,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "bottom" },
                tooltip: {
                  callbacks: {
                    label: (ctx) =>
                      `${ctx.label}: ${Number(ctx.parsed)} issues`,
                  },
                },
                // 3. Add datalabels config for Resolution Type (Doughnut Chart)
                datalabels: {
                  color: "#fff",
                  font: {
                    weight: "bold",
                    size: 10,
                  },
                  formatter: (value) => {
                    // Only show non-zero count
                    return value > 0 ? `${value}` : "";
                  },
                  display: true,
                },
              },
            },
          });
        }
      } catch (e) {
        console.error("Failed to create resolution chart:", e);
      }

      try {
        if (hasCategoryData && categoryChartRef.current) {
          const ctx = categoryChartRef.current.getContext("2d");
          categoryChartInstance.current = new ChartJS(ctx, {
            type: "bar",
            data: {
              labels: categoryEntries.map(([k]) => k),
              datasets: [
                {
                  label: "Percentage",
                  data: categoryEntries.map(([_, v]) => v),
                  backgroundColor: "#3B82F6",
                  borderRadius: 6,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx) =>
                      `${ctx.dataset.label || ""} ${Number(
                        ctx.parsed.y ?? ctx.parsed
                      ).toFixed(2)}%`,
                  },
                },
                // 3. Add datalabels config for Issue Categories (Bar Chart)
                datalabels: {
                  anchor: "end",
                  align: "top",
                  offset: 4,
                  color: "#1F2937", // Dark text color
                  font: {
                    weight: "bold",
                    size: 10,
                  },
                  formatter: (value) => {
                    // Display percentage above the bar
                    return value > 0.01 ? `${value.toFixed(1)}%` : "";
                  },
                  display: true,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (v) => `${v}%`,
                  },
                },
                x: {
                  ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 },
                },
              },
            },
          });
        }
      } catch (e) {}
    }, 60);

    return () => {
      if (createTimeoutRef.current) {
        clearTimeout(createTimeoutRef.current);
        createTimeoutRef.current = null;
      }
      destroyAll();
    };
  }, [
    analytics,
    data,
    categoryEntries,
    hasCategoryData,
    hasResolutionData,
    resolutionEntries,
    createdFrom,
    createdTo,
    issueLevel,
  ]); // Added missing dependencies

  if (loading) {
    return (
      <div className="p-5 text-center">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Fetching Analytics...</p>
      </div>
    );
  }

  if (!analytics || !analytics.data) {
    return (
      <div className="p-5 text-center">
        <p>No analytics found for this selection.</p>
      </div>
    );
  } // Inline styles for Tailwind-like colors

  const badgeStyles = {
    PROCESSING: { backgroundColor: "#DBEAFE", color: "#1E40AF" },
    OPEN: { backgroundColor: "#FEF9C3", color: "#713F12" },
    CLOSED: { backgroundColor: "#DCFCE7", color: "#166534" },
    RESOLVED: { backgroundColor: "#DCFCE7", color: "#166534" },
    DEFAULT: { backgroundColor: "#F3F4F6", color: "#1F2937" },
  };

  const getBadgeStyle = (code) => {
    return badgeStyles[code] || badgeStyles.DEFAULT;
  };

  const chartHeight = "260px";

  return (
    <div className="min-vh-100 bg-light p-3">
      <div className="container">
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h1 className="h3 fw-bold m-0">Issue Analytics</h1>
          <div className="btn-group" role="group" aria-label="Analytics Tabs">
            {["REPORTED", "COUNTERPARTY"].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`btn ${
                  activeTab === tab ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "REPORTED"
                  ? "Reported Issues"
                  : "Counter Party Issues"}
              </button>
            ))}
          </div>
        </div>
        <div className="card shadow-sm border-0 mb-4 p-3 bg-white">
          <div className="row g-3 d-flex align-items-end">
            {/* 1. Issue Level (Left Side, Max width 4 columns) */}
            <div className="col-md-3 col-lg-2">
              <label
                htmlFor="issueLevel"
                className="form-label small fw-bold text-muted mb-1"
              >
                Issue Level
              </label>
              <select
                id="issueLevel"
                className="form-select form-select-sm"
                value={issueLevel}
                onChange={(e) => setIssueLevel(e.target.value)}
              >
                {ISSUE_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level.charAt(0) + level.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Spacer or empty div to push date filters right on large screens */}
            <div className="col-lg-6 d-none d-lg-block"></div>

            {/* 2. Created From (Right Side, smaller width) */}
            <div className="col-md-3 col-lg-2 col-sm-6">
              <label
                htmlFor="createdFrom"
                className="form-label small fw-bold text-muted mb-1"
              >
                Created From
              </label>
              <input
                type="date"
                id="createdFrom"
                className="form-control form-control-sm"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
              />
            </div>

            {/* 3. Created To (Far Right Side, smaller width) */}
            <div className="col-md-3 col-lg-2 col-sm-6">
              <label
                htmlFor="createdTo"
                className="form-label small fw-bold text-muted mb-1"
              >
                Created To
              </label>
              <input
                type="date"
                id="createdTo"
                className="form-control form-control-sm"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="row flex-nowrap overflow-auto g-3">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="col">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body p-3">
                    <Icon className="mb-2 text-primary" size={20} />
                    <h3 className="fw-bold mb-1">{s.value}</h3>
                    <p className="text-muted small mb-0">{s.title}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="row mt-3 g-3">
          <div className="col-lg-4">
            <div
              className="card p-3 shadow-sm h-100"
              style={{ minHeight: "360px" }}
            >
              <h5 className="mb-3">Issue Status</h5>

              <div
                style={{
                  height: chartHeight,
                  width: "100%",
                  position: "relative",
                }}
              >
                <canvas
                  ref={statusChartRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div
              className="card p-3 shadow-sm h-100"
              style={{ minHeight: "360px" }}
            >
              <h5 className="mb-3">Issue Level</h5>

              <div
                style={{
                  height: chartHeight,
                  width: "100%",
                  position: "relative",
                }}
              >
                <canvas
                  ref={levelChartRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div
              className="card p-3 shadow-sm h-100"
              style={{ minHeight: "360px" }}
            >
              <h5 className="mb-3">Resolution Type (Count)</h5>

              <div
                style={{
                  height: chartHeight,
                  width: "100%",
                  position: "relative",
                }}
              >
                {hasResolutionData ? (
                  <canvas
                    ref={resolutionChartRef}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                    No Resolution Data
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row mt-3 g-3">
          {" "}
          {hasCategoryData && (
            <div className="col-lg-6">
              {" "}
              <div
                className="card p-3 shadow-sm h-100"
                style={{ minHeight: 400 }}
              >
                <h5 className="mb-0">Issue Categories</h5>{" "}
                <div style={{ height: 340, width: "100%" }}>
                  {" "}
                  <canvas
                    ref={categoryChartRef}
                    style={{ width: "100%", height: "100%" }}
                  />{" "}
                </div>{" "}
              </div>{" "}
            </div>
          )}{" "}
          <div className={hasCategoryData ? "col-lg-6" : "col-12"}>
            {" "}
            <div
              className="card p-3 shadow-sm h-100"
              style={{ minHeight: 400 }}
            >
              <h5>Pending Issues</h5>{" "}
              {/* Added max-height and overflow for insights list */}
              <div
                className="mt-3"
                style={{ maxHeight: 340, overflowY: "auto" }}
              >
                {" "}
                {data.LatestActionInsights?.length > 0 ? (
                  data.LatestActionInsights.slice(0, 5).map((ins, idx) => (
                    <div className="border rounded p-3 mb-2" key={idx}>
                      {" "}
                      <div className="d-flex justify-content-between align-items-center">
                        {" "}
                        <span className="small text-muted fw-bold">
                          {ins.issue_id}{" "}
                        </span>{" "}
                        <span
                          className="badge"
                          style={getBadgeStyle(ins.lastAction.description.code)}
                        >
                          {" "}
                          {ins.lastAction.description.code}{" "}
                        </span>{" "}
                      </div>{" "}
                      <p className="mt-2 mb-1">
                        {" "}
                        {ins.lastAction.description.short_desc ||
                          ins.lastAction.description.long_desc ||
                          ins.lastAction.description.name ||
                          "No description"}{" "}
                      </p>{" "}
                      <small className="text-muted">
                        Actor: {ins.lastAction.actor_name || "N/A"} | Updated:{" "}
                        {ins.lastAction.updated_at
                          ? new Date(ins.lastAction.updated_at).toLocaleString()
                          : "N/A"}{" "}
                      </small>{" "}
                    </div>
                  ))
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                    No Latest Actions{" "}
                  </div>
                )}{" "}
              </div>{" "}
            </div>{" "}
          </div>{" "}
        </div>{" "}
      </div>{" "}
    </div>
  );
}
